import { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';

import Flex from '@Components/Bases/Flex';
import CompleteSectionForm from '@Components/CompleteSectionForm/CompleteSectionForm';
import { defaultCompletedSection } from '@Domains/utils';
import {
  enabledGeneralInfoCompletedSection,
  enabledMarketingIndustryCompletedSectionEn,
  enabledMarketingIndustryCompletedSectionTh,
  enabledProductInfoCompletedSectionEn,
  enabledProductInfoCompletedSectionTh,
} from '@Router/NatureOfBusiness/completedSectionUtils';
import { formatToShortThaiDateTime } from '@Utils/datetime';
import {
  validateEnglishDescription,
  validateThaiDescription,
} from '@Utils/validation';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useNatureOfBusinessForm } from '../NatureOfBusinessFormProvider';
import {
  CorporateInformationEnglishFormValues,
  CorporateInformationThaiFormValues,
  INatureOfBusinessFormValues,
} from '../NatureOfBusinessFormProvider/types';
import { Role } from 'src/Enum/Report';
import {
  enabledIncomeStructureV2CompletedSectionEn,
  enabledIncomeStructureV2CompletedSectionTh,
} from '../IncomeStructure/utils';
import {
  checkValidIntroductionEn,
  checkValidIntroductionTh,
  checkValidRemarkEn,
  checkValidRemarkTh,
} from '@Utils/validationIntroRemark';

const NatureOfBusinessCompletedSection = () => {
  const { isPublished } = useProgressBar();
  const { getValues, setValue } = useNatureOfBusinessForm();
  const {
    corporateInformation,
    productInformation,
    marketingIndustry,
    incomeStructure,
    introduction,
    remark,
  } = getValues() ?? ({} as INatureOfBusinessFormValues);

  const completedSection =
    useWatch({ name: 'completedSection' }) || defaultCompletedSection;

  const otherDescriptions = useWatch({ name: 'otherDescriptions' });

  const corporateCompletedSection = useWatch({
    name: 'corporateInformation.completedSection',
  });
  const productCompletedSection = useWatch({
    name: 'productInformation.completedSection',
  });
  const marketingCompletedSection = useWatch({
    name: 'marketingIndustry.completedSection',
  });
  const incomeCompletedSection = useWatch({
    name: 'incomeStructure.completedSection',
  });

  useEffect(() => {
    const isCompletedTh =
      corporateCompletedSection?.isCompletedTh &&
      productCompletedSection?.isCompletedTh &&
      marketingCompletedSection?.isCompletedTh &&
      incomeCompletedSection?.isCompletedTh;

    const isCompletedEn =
      corporateCompletedSection?.isCompletedEn &&
      productCompletedSection?.isCompletedEn &&
      marketingCompletedSection?.isCompletedEn &&
      incomeCompletedSection?.isCompletedEn;

    const updateCompletedSection = (
      isThCompleted: boolean,
      isEnCompleted: boolean
    ) => {
      setValue('completedSection.isCompletedTh', isThCompleted);
      setValue('completedSection.isCompletedEn', isEnCompleted);
    };

    if (otherDescriptions?.length === 0) {
      updateCompletedSection(isCompletedTh, isCompletedEn);
    } else {
      const isThValid =
        isCompletedTh && validateThaiDescription(otherDescriptions);
      const isEnValid =
        isCompletedEn && validateEnglishDescription(otherDescriptions);
      updateCompletedSection(isThValid, isEnValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    corporateCompletedSection,
    productCompletedSection,
    marketingCompletedSection,
    incomeCompletedSection,
    setValue,
  ]);

  const enabledCompletedSectionTh = () => {
    let enabledTh = true;

    const generalInfoTh = {
      ...corporateInformation?.thai,
      introductionTh: corporateInformation?.introduction?.descriptionTh,
      remarkTh: corporateInformation?.remark?.descriptionTh,
    } as CorporateInformationThaiFormValues;

    if (
      corporateInformation &&
      !enabledGeneralInfoCompletedSection(generalInfoTh)
    ) {
      enabledTh = false;
    }

    if (
      productInformation &&
      !enabledProductInfoCompletedSectionTh(productInformation)
    ) {
      enabledTh = false;
    }

    if (
      marketingIndustry &&
      !enabledMarketingIndustryCompletedSectionTh(
        marketingIndustry?.marketingPolicy
      )
    ) {
      enabledTh = false;
    }

    if (
      incomeStructure &&
      !enabledIncomeStructureV2CompletedSectionTh(incomeStructure)
    ) {
      enabledTh = false;
    }

    if (otherDescriptions && !validateThaiDescription(otherDescriptions)) {
      enabledTh = false;
    }

    if (introduction && !checkValidIntroductionTh(introduction)) {
      enabledTh = false;
    }

    if (remark && !checkValidRemarkTh(remark)) {
      enabledTh = false;
    }

    return enabledTh;
  };

  const enabledCompletedSectionEn = () => {
    let enabledEn = true;

    const generalInfoEn = {
      ...corporateInformation?.english,
      introductionEn: corporateInformation?.introduction?.descriptionEn,
      remarkEn: corporateInformation?.remark?.descriptionEn,
    } as CorporateInformationEnglishFormValues;

    if (
      corporateInformation &&
      !enabledGeneralInfoCompletedSection(generalInfoEn)
    ) {
      enabledEn = false;
    }

    if (
      productInformation &&
      !enabledProductInfoCompletedSectionEn(productInformation)
    ) {
      enabledEn = false;
    }

    if (
      marketingIndustry &&
      !enabledMarketingIndustryCompletedSectionEn(
        marketingIndustry?.marketingPolicy
      )
    ) {
      enabledEn = false;
    }

    if (
      incomeStructure &&
      !enabledIncomeStructureV2CompletedSectionEn(incomeStructure)
    ) {
      enabledEn = false;
    }

    if (otherDescriptions && !validateEnglishDescription(otherDescriptions)) {
      enabledEn = false;
    }

    if (introduction && !checkValidIntroductionEn(introduction)) {
      enabledEn = false;
    }

    if (remark && !checkValidRemarkEn(remark)) {
      enabledEn = false;
    }

    return enabledEn;
  };

  const onThChange = (isAllCompletedTh?: boolean) => {
    if (!isAllCompletedTh) {
      setValue('corporateInformation.completedSection.roleStampTh', Role.NONE);
      setValue('corporateInformation.completedSection.dateTh', undefined);
      setValue('productInformation.completedSection.roleStampTh', Role.NONE);
      setValue('productInformation.completedSection.dateTh', undefined);
      setValue('marketingIndustry.completedSection.roleStampTh', Role.NONE);
      setValue('marketingIndustry.completedSection.dateTh', undefined);
      setValue('incomeStructure.completedSection.roleStampTh', Role.NONE);
      setValue('incomeStructure.completedSection.dateTh', undefined);
    }

    setValue(
      'corporateInformation.completedSection.isCompletedTh',
      isAllCompletedTh
    );
    setValue(
      'productInformation.completedSection.isCompletedTh',
      isAllCompletedTh
    );
    setValue(
      'marketingIndustry.completedSection.isCompletedTh',
      isAllCompletedTh
    );
    setValue(
      'incomeStructure.completedSection.isCompletedTh',
      isAllCompletedTh
    );
  };

  const onEnChange = (isAllCompletedEn?: boolean) => {
    if (!isAllCompletedEn) {
      setValue('corporateInformation.completedSection.roleStampEn', Role.NONE);
      setValue('corporateInformation.completedSection.dateEn', undefined);
      setValue('productInformation.completedSection.roleStampEn', Role.NONE);
      setValue('productInformation.completedSection.dateEn', undefined);
      setValue('marketingIndustry.completedSection.roleStampEn', Role.NONE);
      setValue('marketingIndustry.completedSection.dateEn', undefined);
      setValue('incomeStructure.completedSection.roleStampEn', Role.NONE);
      setValue('incomeStructure.completedSection.dateEn', undefined);
    }

    setValue(
      'corporateInformation.completedSection.isCompletedEn',
      isAllCompletedEn
    );
    setValue(
      'productInformation.completedSection.isCompletedEn',
      isAllCompletedEn
    );
    setValue(
      'marketingIndustry.completedSection.isCompletedEn',
      isAllCompletedEn
    );
    setValue(
      'incomeStructure.completedSection.isCompletedEn',
      isAllCompletedEn
    );
  };

  const formattedCompletedSectionDateTh = completedSection.dateTh
    ? formatToShortThaiDateTime(completedSection.dateTh)
    : '';
  const formattedCompletedSectionDateEn = completedSection.dateEn
    ? formatToShortThaiDateTime(completedSection.dateEn)
    : '';

  return (
    <Box bgcolor={'common.white'}>
      <Flex
        justifyContent={'space-between'}
        alignItems={'center'}
        pt={3}
        px={2}
      >
        <Typography fontWeight={'bold'}>
          กรณีต้องการยืนยันความถูกต้องทุกหัวข้อ
        </Typography>
      </Flex>
      <CompleteSectionForm
        label={`ส่วนการยืนยันการตรวจสอบข้อมูลครบถ้วนทุกหัวข้อ`}
        checkSectionName={'completedSection'}
        enableCheckSectionTh={!isPublished && enabledCompletedSectionTh()}
        enableCheckSectionEn={!isPublished && enabledCompletedSectionEn()}
        onCompleteThChange={onThChange}
        onCompleteEnChange={onEnChange}
        formattedCompletedSectionDateTh={formattedCompletedSectionDateTh}
        formattedCompletedSectionDateEn={formattedCompletedSectionDateEn}
      />
    </Box>
  );
};

export default NatureOfBusinessCompletedSection;
